import { SessionConfirmationModal } from '../../../../components/Sessions/SessionConfirmationModal';
import { showErrorToast, showSuccessToast } from '../../../../components/PluginsMenu/Toastr/Toastr';
import { SessionDetails } from '../../../../components/Sessions/SessionDetails';
import { formatDateToISOString, parseDate } from '../../../../../utils/utils';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import BookingWizard from './book-session-wizard';
import { Card, Row, Col } from 'react-bootstrap';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import 'react-calendar/dist/Calendar.css';
import '../../../../styles/calendar.css';
import Calendar from 'react-calendar';
import DatePicker from 'react-datepicker';
import { proficiencyLabels, subjects } from '../../../../../dummy';
import { useGetSessionDurationQuery, useBookSlotSessionMutation, useGetAvailableSlotsQuery } from '../../../../../store/services/student/SessionService';
import { parseISO } from 'date-fns';
import { trackMetaEvent } from '../../../../utils/metaPixel';
import { pushDataLayerEvent } from '../../../../utils/gtm';

export const BookSession = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const tutorId = searchParams.get('tutorId');
    const subject = location.state?.subject;
    const subjectId = location.state?.subjectId;

    const { data: sessionDurations, isLoading: isLoadingDurations } = useGetSessionDurationQuery();
    const [bookSlotSession, { isLoading: isBookingLoading }] = useBookSlotSessionMutation();

    const [selectedDate, setSelectedDate] = useState(() => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(12, 0, 0, 0); // Set to noon to avoid timezone issues
        return tomorrow;
    });
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [optionalMessage, setOptionalMessage] = useState('');
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [isBookingInProgress, setIsBookingInProgress] = useState(false);
    const [selectedDuration, setSelectedDuration] = useState(null);
    const [allSlots, setAllSlots] = useState([]); // Store all slots data
    const lastAvailabilityTrackingKeyRef = useRef(null);

    // Fetch ALL slots once when tutor and duration are selected
    const { data: allSlotsData, isLoading: isLoadingSlots } = useGetAvailableSlotsQuery(
        {
            TutorId: tutorId,
            SlotPricingId: selectedDuration?.id,
            // Don't pass date parameter to get all slots
        },
        { 
            skip: !tutorId || !selectedDuration,
            refetchOnMountOrArgChange: false // Only fetch once
        }
    );

    // Store all slots when data is fetched
    useEffect(() => {
        if (allSlotsData?.data) {
            setAllSlots(allSlotsData.data);

            // Track availability fetched -> Meta Lead + GTM tutor_availability_view
            // Fire once per backend-provided event ids to avoid duplicates on rerenders.
            const metaEventId =
                allSlotsData?.data?.metaEventId ?? allSlotsData?.metaEventId;
            const googleEventId =
                allSlotsData?.data?.googleEventId ?? allSlotsData?.googleEventId;

            const trackingKey = `${tutorId || ""}|${selectedDuration?.id || ""}|${metaEventId || ""}|${googleEventId || ""}`;
            if (trackingKey && lastAvailabilityTrackingKeyRef.current !== trackingKey) {
                lastAvailabilityTrackingKeyRef.current = trackingKey;

                if (metaEventId) {
                    trackMetaEvent(
                        "Lead",
                        {
                            content_name: "Tutor Availability",
                            tutor_id: tutorId,
                            slot_pricing_id: selectedDuration?.id,
                        },
                        metaEventId
                    );
                }

                pushDataLayerEvent("tutor_availability_view", {
                    google_event_id: googleEventId,
                    tutor_id: tutorId,
                    slot_pricing_id: selectedDuration?.id,
                });
            }
        }
    }, [allSlotsData, tutorId, selectedDuration?.id]);

    // Filter slots for the selected date
    const filteredSlotsForSelectedDate = useMemo(() => {
        if (!selectedDate || allSlots.length === 0) return [];
        
        const formattedSelectedDate = formatDateToISOString(selectedDate);
        const matchedDay = allSlots?.slots?.find(d => d.day === formattedSelectedDate);
        
        return matchedDay?.slots || [];
    }, [selectedDate, allSlots]);

    // Get available days from all slots
    const availableDays = useMemo(() => {
        return allSlots?.slots?.map(d => d.day) || [];
    }, [allSlots]);

    // Get min and max available dates
    const minAvailableDate = useMemo(() => {
        if (availableDays.length > 0) {
            return parseISO(availableDays[0]);
        }
        // Fallback to tomorrow if no slots available
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);
        return tomorrow;
    }, [availableDays]);

    const maxAvailableDate = useMemo(() => {
        if (availableDays.length > 0) {
            return parseISO(availableDays[availableDays.length - 1]);
        }
        return null;
    }, [availableDays]);

    // Ensure selected date is within available range
    useEffect(() => {
        if (availableDays.length > 0) {
            const minDate = parseISO(availableDays[0]);
            const maxDate = parseISO(availableDays[availableDays.length - 1]);
            
            if (selectedDate < minDate || selectedDate > maxDate) {
                setSelectedDate(minDate);
                setSelectedSlot(null);
            }
        }
    }, [availableDays, selectedDate]);

    useEffect(() => {
        if (!tutorId) {
            navigate(-1);
        }
    }, [tutorId, navigate]);

    // Add function to check if a date has available slots
    const dateHasAvailableSlots = (date) => {
        const formattedDate = formatDateToISOString(date);
        const dayData = allSlots?.slots?.find(d => d.day === formattedDate);
        
        if (!dayData) return false;
        
        // Check if there are any available slots (not booked)
        return dayData.slots.some(slot => slot.sessionStatus === "Available");
    };

    const handleDateSelect = (date) => {
        setSelectedDate(date);
        setSelectedSlot(null);
    };

    const handleSlotClick = (slot) => {
        if (slot === null) {
            setSelectedSlot(null);
        } else {
            setSelectedSlot({ 
                date: selectedDate,
                duration: selectedDuration?.duration, 
                ...slot 
            });
        }
    };

    const removeSelectedSlot = () => {
        setSelectedSlot(null);
    }

    const updateSlotSubject = (subject) => {
        setSelectedSlot(prev => ({ ...prev, subject }));
    };

    const updateSlotProficiency = (proficiency) => {
        setSelectedSlot(prev => ({ ...prev, proficiency }));
    };

    const handleBookSlot = () => {

        if (!selectedSlot.proficiency) {
            showErrorToast("Please rate your language proficiency for this session.");
            return;
        }

        setShowConfirmationModal(true);
    };

    // Handle booking confirmation
    const handleCheckout = async () => {
        setIsBookingInProgress(true);

        try {
            // FIX: Ensure the date is formatted correctly
            const bookingDate = new Date(selectedSlot.date);
            
            // Make sure we're using the correct date (selectedDate, not selectedSlot.date)
            const correctDate = selectedDate || bookingDate;
            
            // Format the date to YYYY-MM-DD format (without time)
            // const formattedDate = correctDate.toISOString().split('T')[0];
            const formattedDate = formatDateToISOString(correctDate);
            
            // Prepare the booking payload with proper date formatting
            const bookingPayload = {
                availabilitySlotId: selectedSlot.slotId,
                date: formattedDate, // Use YYYY-MM-DD format
                startTime: selectedSlot.startTime,
                endTime: selectedSlot.endTime,
                subject: subjectId,
                proficiency: String(selectedSlot.proficiency),
                message: optionalMessage
            };

            console.log("Selected Date:", selectedDate);
            console.log("Selected Slot Date:", selectedSlot.date);
            console.log("Selected Correct Date:", correctDate.toISOString());
            console.log("Formatted Date for API:", formattedDate);
            console.log("Booking Payload:", bookingPayload);

            // Call the API
            const response = await bookSlotSession(bookingPayload).unwrap();

            if (response.succeeded) {
                const metaEventId = response?.data?.metaEventId;
                if (metaEventId) {
                    trackMetaEvent(
                        "InitiateCheckout",
                        {
                            content_name: "Session Booking",
                            tutor_id: tutorId,
                            subject_id: subjectId,
                        },
                        metaEventId
                    );
                }
                const googleEventId = response?.data?.googleEventId;
                pushDataLayerEvent("checkout_started", {
                    google_event_id: googleEventId,
                    tutor_id: tutorId,
                    subject_id: subjectId,
                });

                showSuccessToast("Redirecting you to the payment page");
                setIsBookingInProgress(false);
                setShowConfirmationModal(false);

                // Redirect to the payment URL returned by the API
                window.location.href = response.data?.checkoutUrl;
                
                setSelectedSlot(null);
                setOptionalMessage('');
            } else {
                throw new Error(response.errors?.join(', ') || 'Booking failed');
            }
        } catch (error) {
            console.error("Error booking session:", error);
            setIsBookingInProgress(false);
            showErrorToast(error.data?.errors?.join(', ') || "Failed to book session. Please try again.");
        }
    };

    if (!tutorId) {
        return (
            <div className="container-fluid py-5">
                <div className="alert alert-warning text-center">
                    <h4>No Tutor Selected</h4>
                    <p>Please select a tutor first to book a session.</p>
                    <button className="btn btn-primary" onClick={() => navigate(-1)}>
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-5">
            <div className="mb-3">
                <button className="btn p-0 d-flex align-items-center"  onClick={() => navigate(-1)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#000000" className="bi bi-arrow-left me-2" viewBox="0 0 16 16">
                        <path fillRule="evenodd" d="M15 8a.5.5 0 0 1-.5.5H2.707l4.147 4.146a.5.5 0 0 1-.708.708l-5-5a.5.5 0 0 1 0-.708l5-5a.5.5 0 1 1 .708.708L2.707 7.5H14.5A.5.5 0 0 1 15 8z"/>
                    </svg>
                </button>
            </div>
            <Card className="shadow-lg rounded-lg">
                <Card.Header className="bg-primary text-white py-3">
                    <div className="d-flex justify-content-between align-items-center">
                        <h4 className="mb-0 text-white">Book a Tutoring Session</h4>
                    </div>
                </Card.Header>

                <Card.Body>
                    <Row>
                        <Col lg='5'>
                            <div className="calendar-container event-calender dz-calender">
                                {/* <Calendar
                                    onChange={handleDateSelect}
                                    value={selectedDate ? new Date(selectedDate) : null}
                                    tileContent={tileContent}
                                    minDate={new Date()}
                                    tileDisabled={({ date }) => date < new Date().setHours(0, 0, 0, 0)}
                                    tileClassName={({ date, view }) => {
                                        const dateString = formatDateToISOString(date);
                                        return dateString === selectedDate ? 'selected-date' : null;
                                    }}
                                /> */}
                                <DatePicker
                                    selected={selectedDate}
                                    onChange={handleDateSelect}
                                    inline
                                    highlightDates={[selectedDate]}
                                    shouldCloseOnSelect={false}
                                    minDate={(() => {
                                        const tomorrow = new Date();
                                        tomorrow.setDate(tomorrow.getDate() + 1);
                                        tomorrow.setHours(0, 0, 0, 0);
                                        return tomorrow;
                                    })()}
                                    maxDate={maxAvailableDate}
                                    filterDate={(date) => {
                                        // Disable dates before tomorrow
                                        const tomorrow = new Date();
                                        tomorrow.setDate(tomorrow.getDate() + 1);
                                        tomorrow.setHours(0, 0, 0, 0);
                                        
                                        const currentDate = new Date(date);
                                        currentDate.setHours(0, 0, 0, 0);
                                        
                                        // Check if date is tomorrow or later
                                        if (currentDate < tomorrow) return false;
                                        
                                        // Check if date is within available range
                                        if (maxAvailableDate && currentDate > maxAvailableDate) return false;
                                        
                                        // Check if date has available slots
                                        return dateHasAvailableSlots(date);
                                    }}
                                    dayClassName={(date) => {
                                        const currentDate = new Date(date);
                                        currentDate.setHours(0, 0, 0, 0);
                                        
                                        if (!dateHasAvailableSlots(date)) {
                                            return 'text-muted disabled-date';
                                        }
                                        
                                        const formattedDate = formatDateToISOString(date);
                                        const selectedFormattedDate = formatDateToISOString(selectedDate);
                                        
                                        return formattedDate === selectedFormattedDate ? 'selected-date' : '';
                                    }}
                                />
                                {!selectedDate && (
                                    <div className="alert alert-info mt-3">
                                        Please select a date to see available time slots.
                                    </div>
                                )}
                            </div>
                        </Col>
                        <Col lg='7'>
                            {!selectedDate ? (
                                <div className="alert alert-warning">
                                    Please select a date first to see available durations and time slots.
                                </div>
                            ) : isLoadingDurations ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : (
                                <BookingWizard 
                                    handleSlotClick={handleSlotClick} 
                                    selectedDuration={selectedDuration} 
                                    setSelectedDuration={setSelectedDuration} 
                                    removeSelectedSlot={removeSelectedSlot}
                                    sessionDurations={sessionDurations?.data || []}
                                    selectedDate={selectedDate}
                                    tutorId={tutorId}
                                    selectedSlot={selectedSlot}
                                    filteredSlots={filteredSlotsForSelectedDate} // Add this
                                    isLoadingSlots={isLoadingSlots} // Add loading state
                                />
                            )}
                        </Col>
                    </Row>
                </Card.Body>

                {selectedSlot && (
                    <SessionDetails
                        selectedSlot={selectedSlot}
                        subject={subject}
                        updateSlotSubject={updateSlotSubject}
                        updateSlotProficiency={updateSlotProficiency}
                        optionalMessage={optionalMessage}
                        setOptionalMessage={setOptionalMessage}
                        handleBookSlot={handleBookSlot}
                        isBookingLoading={isBookingLoading}
                    />
                )}
            </Card>

            <SessionConfirmationModal
                showConfirmationModal={showConfirmationModal}
                isBookingInProgress={isBookingInProgress || isBookingLoading}
                setShowConfirmationModal={setShowConfirmationModal}
                handleCheckout={handleCheckout}
                selectedSlot={selectedSlot}
                subject={subject}
                proficiencyLabels={proficiencyLabels}
                optionalMessage={optionalMessage}
            />
        </div>
    );
};